import { Controller } from "@hotwired/stimulus"

// Holi-style colorful particle trail effect.
// Controlled server-side via PARTICLE_TRAIL_ENABLED env var.
// Canvas overlay with multi-lobe radial gradient particles.
export default class extends Controller {
  static values = { enabled: { type: Boolean, default: true } }

  // --- constants ---
  COLOR_RGBS = [
    { r: 255, g: 93, b: 143 },   // pink
    { r: 255, g: 209, b: 102 },  // yellow
    { r: 123, g: 211, b: 137 },  // green
    { r: 92, g: 168, b: 255 },   // blue
    { r: 187, g: 134, b: 252 },  // purple
  ]
  GRAVITY = 0.022
  DRAG = 0.99

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    if (!this.enabledValue) return

    this._detectPointer()
    this._buildProfile()
    this._createCanvas()
    this._bindEvents()
    this._resetState()
  }

  disconnect() {
    this._removeEvents()
    this._destroyCanvas()
    if (this._rafId) {
      cancelAnimationFrame(this._rafId)
      this._rafId = 0
    }
  }

  // --- setup ---

  _detectPointer() {
    const fine = window.matchMedia("(any-pointer: fine)").matches
    const coarse = window.matchMedia("(any-pointer: coarse)").matches ||
                   window.matchMedia("(pointer: coarse)").matches ||
                   navigator.maxTouchPoints > 0
    this._hasFine = fine
    this._hasCoarse = coarse
    this._isTouchOnly = coarse && !fine
  }

  _buildProfile() {
    if (this._isTouchOnly) {
      this._profile = {
        maxParticles: 90, maxDpr: 1.25, touchMoveIntervalMs: 60,
        touchTrailCount: 2, touchTrailVelocity: 0.4,
        interactiveBurst: 12, normalBurst: 7,
        clickVelocity: 0.95, touchVelocity: 0.95,
        life: [22, 40], size: [2.6, 6.4], lobes: [2, 5],
        spread: [0.05, 0.13], opacity: [0.18, 0.4],
      }
    } else {
      this._profile = {
        maxParticles: 72, maxDpr: 1.5, touchMoveIntervalMs: 42,
        touchTrailCount: 1, touchTrailVelocity: 0.48,
        interactiveBurst: 4, normalBurst: 2,
        clickVelocity: 0.85, touchVelocity: 1.1,
        life: [24, 40], size: [2.8, 6.8], lobes: [2, 4],
        spread: [0.06, 0.14], opacity: [0.22, 0.4],
      }
    }
  }

  _createCanvas() {
    this._canvas = document.createElement("canvas")
    this._canvas.setAttribute("aria-hidden", "true")
    Object.assign(this._canvas.style, {
      position: "fixed", inset: "0",
      pointerEvents: "none", zIndex: "1050",
    })
    this._ctx = this._canvas.getContext("2d")
    if (!this._ctx) return
    document.body.appendChild(this._canvas)
    this._resizeCanvas()
  }

  _destroyCanvas() {
    if (this._canvas && this._canvas.parentNode) {
      this._canvas.parentNode.removeChild(this._canvas)
    }
    this._canvas = null
    this._ctx = null
  }

  _resetState() {
    this._particles = []
    this._rafId = 0
    this._lastFrameAt = 0
    this._slowFrameScore = 0
    this._lastPointerMoveAt = 0
    this._lastTouchMoveAt = 0
    this._lastTouchAt = 0
    this._lastClickAt = 0
  }

  // --- events ---

  _bindEvents() {
    this._onResize = this._resizeCanvas.bind(this)
    this._onPointerMove = this._handlePointerMove.bind(this)
    this._onTouchMove = this._handleTouchMove.bind(this)
    this._onTouchStart = this._handleTouchStart.bind(this)
    this._onClick = this._handleClick.bind(this)

    window.addEventListener("resize", this._onResize)
    if (this._hasFine) {
      window.addEventListener("pointermove", this._onPointerMove, { passive: true })
    }
    if (this._hasCoarse) {
      window.addEventListener("touchmove", this._onTouchMove, { passive: true })
      window.addEventListener("touchstart", this._onTouchStart, { passive: true })
    }
    window.addEventListener("click", this._onClick, { passive: true })
  }

  _removeEvents() {
    window.removeEventListener("resize", this._onResize)
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("touchmove", this._onTouchMove)
    window.removeEventListener("touchstart", this._onTouchStart)
    window.removeEventListener("click", this._onClick)
  }

  // --- canvas ---

  _resizeCanvas() {
    if (!this._canvas || !this._ctx) return
    const dpr = Math.min(window.devicePixelRatio || 1, this._profile.maxDpr)
    this._width = window.innerWidth
    this._height = window.innerHeight
    this._canvas.width = Math.floor(this._width * dpr)
    this._canvas.height = Math.floor(this._height * dpr)
    this._canvas.style.width = `${this._width}px`
    this._canvas.style.height = `${this._height}px`
    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  // --- particle creation ---

  _randomBetween(min, max) { return Math.random() * (max - min) + min }

  _pickColor() { return this.COLOR_RGBS[Math.floor(Math.random() * this.COLOR_RGBS.length)] }

  _createLobes() {
    const p = this._profile
    const count = Math.floor(this._randomBetween(p.lobes[0], p.lobes[1] + 1))
    const lobes = []
    for (let i = 0; i < count; i++) {
      lobes.push({
        dx: this._randomBetween(-0.9, 0.9),
        dy: this._randomBetween(-0.9, 0.9),
        scale: this._randomBetween(0.5, 1),
        stretchX: this._randomBetween(0.75, 1.35),
        stretchY: this._randomBetween(0.75, 1.35),
      })
    }
    return lobes
  }

  _addParticle(x, y, velocityScale) {
    const p = this._profile
    const angle = this._randomBetween(-Math.PI, Math.PI)
    const speed = this._randomBetween(0.45, 1.6) * velocityScale
    const life = this._randomBetween(p.life[0], p.life[1])
    const rgb = this._pickColor()

    this._particles.push({
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: this._randomBetween(p.size[0], p.size[1]),
      spread: this._randomBetween(p.spread[0], p.spread[1]),
      opacity: this._randomBetween(p.opacity[0], p.opacity[1]),
      rotation: this._randomBetween(0, Math.PI * 2),
      spin: this._randomBetween(-0.025, 0.025),
      life, maxLife: life,
      lobes: this._createLobes(),
      colorStart: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.92)`,
      colorMid: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.48)`,
      colorEnd: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`,
    })
  }

  _burst(x, y, count, velocityScale) {
    for (let i = 0; i < count; i++) this._addParticle(x, y, velocityScale)
    this._trimParticles()
    if (!this._rafId) this._rafId = requestAnimationFrame(this._animate.bind(this))
  }

  _trimParticles() {
    const max = this._profile.maxParticles
    if (this._particles.length > max) {
      this._particles.splice(0, this._particles.length - max)
    }
  }

  // --- rendering ---

  _drawParticle(particle, lobeStep) {
    const alpha = (particle.life / particle.maxLife) * particle.opacity
    if (alpha <= 0.02) return

    const ctx = this._ctx
    ctx.globalAlpha = alpha
    ctx.save()
    ctx.translate(particle.x, particle.y)
    ctx.rotate(particle.rotation)

    for (let i = 0; i < particle.lobes.length; i += lobeStep) {
      const lobe = particle.lobes[i]
      const cx = lobe.dx * particle.size * 0.65
      const cy = lobe.dy * particle.size * 0.65
      const rx = particle.size * lobe.scale * lobe.stretchX
      const ry = particle.size * lobe.scale * lobe.stretchY
      const outerR = Math.max(rx, ry)

      const grad = ctx.createRadialGradient(
        cx - outerR * 0.2, cy - outerR * 0.2, outerR * 0.1,
        cx, cy, outerR
      )
      grad.addColorStop(0, particle.colorStart)
      grad.addColorStop(0.45, particle.colorMid)
      grad.addColorStop(1, particle.colorEnd)

      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.restore()
  }

  _animate() {
    const now = performance.now()
    if (this._lastFrameAt > 0) {
      const delta = now - this._lastFrameAt
      this._slowFrameScore = delta > 24
        ? Math.min(this._slowFrameScore + 1, 24)
        : Math.max(this._slowFrameScore - 1, 0)
    }
    this._lastFrameAt = now

    this._ctx.clearRect(0, 0, this._width, this._height)

    const lobeStep = this._slowFrameScore > 10 ? 2 : 1
    for (let i = this._particles.length - 1; i >= 0; i--) {
      const p = this._particles[i]
      p.life -= 1
      if (p.life <= 0) { this._particles.splice(i, 1); continue }
      p.vx *= this.DRAG
      p.vy = p.vy * this.DRAG + this.GRAVITY
      p.x += p.vx
      p.y += p.vy
      p.size += p.spread
      p.rotation += p.spin
      this._drawParticle(p, lobeStep)
    }

    this._ctx.globalAlpha = 1

    if (this._particles.length) {
      this._rafId = requestAnimationFrame(this._animate.bind(this))
    } else {
      this._rafId = 0
      this._lastFrameAt = 0
      this._slowFrameScore = 0
    }
  }

  // --- event handlers ---

  _isInteractive(target) {
    return target instanceof Element &&
      !!target.closest("button, .button, a, section, [data-result-table]")
  }

  _getBurstCount(target) {
    return this._isInteractive(target) ? this._profile.interactiveBurst : this._profile.normalBurst
  }

  _handlePointerMove(event) {
    if (event.pointerType && event.pointerType !== "mouse") return
    const now = performance.now()
    const interval = Math.min(56 + this._slowFrameScore * 2, 100)
    if (now - this._lastPointerMoveAt < interval) return
    const load = this._particles.length / this._profile.maxParticles
    if (load > 0.72) return
    this._lastPointerMoveAt = now
    const count = load < 0.28 && this._slowFrameScore < 5 ? 2 : 1
    this._burst(event.clientX, event.clientY, count, count === 2 ? 0.47 : 0.38)
  }

  _handleTouchMove(event) {
    const touch = event.touches[0]
    if (!touch) return
    const now = performance.now()
    if (now - this._lastTouchMoveAt < this._profile.touchMoveIntervalMs) return
    this._lastTouchMoveAt = now
    this._lastTouchAt = now
    this._burst(touch.clientX, touch.clientY, this._profile.touchTrailCount, this._profile.touchTrailVelocity)
  }

  _handleTouchStart(event) {
    const touch = event.touches[0]
    if (!touch) return
    this._lastTouchAt = performance.now()
    this._burst(touch.clientX, touch.clientY, this._getBurstCount(event.target), this._profile.touchVelocity)
  }

  _handleClick(event) {
    const now = performance.now()
    if (now - this._lastTouchAt < 500) return
    const cooldown = this._isTouchOnly ? 0 : 120
    if (now - this._lastClickAt < cooldown) return
    this._lastClickAt = now
    this._burst(event.clientX, event.clientY, this._getBurstCount(event.target), this._profile.clickVelocity)
  }
}
